/*
Purpose: Casefile-level "Spots" tab that surfaces similar jobs OUTSIDE this casefile.
Approach:
- Use UnifiedResultsPanel directly in the tab for inline search capabilities
- Support evidence-based and content-based matching
- Flexible user controls for investigative needs
*/
import React, { useEffect, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SingleOrderResults from '../modals/results/SingleOrderResults';
import UnifiedResultsPanel from '../results/panels/UnifiedResultsPanel';
import { createContext } from '../modals/results/contexts/modalContexts';
import '../../styles/SpotsTab.css';

// This search logic has been moved to EnhancedSpotsSearchProvider

const CasefileSpotsTab = ({ casefileId, currentUser }) => {
  const [jobs, setJobs] = useState([]); // jobs in this casefile
  const [viewState, setViewState] = useState({ mode: 'list', orderId: null, jobId: null });
  const [evidenceOptions, setEvidenceOptions] = useState([]); // [{id, fileName}]
  const [spotsContext, setSpotsContext] = useState(null);
  const [casefileName, setCasefileName] = useState(null);

  // Load this casefile's jobs for the current user
  useEffect(() => {
    if (!currentUser?.uid || !casefileId) return;
    const q = query(
      collection(db, 'jobs'),
      where('userId', '==', currentUser.uid),
      where('casefileId', '==', casefileId)
    );
    const unsub = onSnapshot(q, (snap) => {
      const all = [];
      snap.forEach(doc => all.push({ id: doc.id, ...doc.data() }));
      // Sort by creation date
      const sorted = all.sort((a, b) => {
        const ad = a.createdAt?.toDate?.() || new Date(0);
        const bd = b.createdAt?.toDate?.() || new Date(0);
        return bd - ad;
      });
      setJobs(sorted);
    });
    return () => unsub();
  }, [casefileId, currentUser?.uid]);

  // Fetch casefile name for proper display
  useEffect(() => {
    if (!casefileId) return;
    
    const fetchCasefileName = async () => {
      try {
        const casefileDoc = await getDoc(doc(db, 'casefiles', casefileId));
        if (casefileDoc.exists()) {
          const casefileData = casefileDoc.data();
          setCasefileName(casefileData.title || casefileData.name || 'Untitled Case');
        }
      } catch (error) {
        console.error('Error fetching casefile name:', error);
        setCasefileName('Unknown Case');
      }
    };
    
    fetchCasefileName();
  }, [casefileId]);

  // Load evidence list for this casefile (to power evidence filter)
  // Load FULL evidence data including downloadURL for proper video display
  useEffect(() => {
    let mounted = true;
    const loadEvidence = async () => {
      try {
        const snap = await getDocs(collection(db, 'casefiles', casefileId, 'evidence'));
        if (!mounted) return;
        const items = [];
        snap.forEach(d => {
          const data = d.data();
          items.push({ 
            id: d.id, 
            fileName: data?.fileName || data?.name || d.id,
            // Preserve the parent casefile ID so downstream components can resolve public/private correctly
            casefileId,
            // Include all properties needed for video display
            downloadURL: data?.downloadURL,
            thumbnailURL: data?.thumbnailURL,
            fileSize: data?.fileSize,
            contentType: data?.contentType,
            filePath: data?.filePath,
            createdAt: data?.createdAt || data?.uploadDate, // UniversalEvidenceWindow expects createdAt
            uploadDate: data?.uploadDate || data?.createdAt,
            duration: data?.duration,
            resolution: data?.resolution,
            type: data?.type,
            summary: data?.summary,
            isPublic: data?.isPublic,
            createdBy: data?.createdBy,
            // Keep the full data object for any other properties
            ...data
          });
        });
        console.log('Loaded evidence options:', items.map(e => ({
          fileName: e.fileName,
          hasDownloadURL: !!e.downloadURL,
          downloadURL: e.downloadURL?.substring(0, 50) + '...'
        })));
        setEvidenceOptions(items);
      } catch (e) {
        console.error('Failed to load evidence for casefile', e);
      }
    };
    if (casefileId) loadEvidence();
    return () => { mounted = false; };
  }, [casefileId]);

  // Create spots context when data is ready
  useEffect(() => {
    if (!currentUser?.uid || !casefileId || evidenceOptions.length === 0 || !casefileName) {
      setSpotsContext(null);
      return;
    }

    try {
      const context = createContext('spots', {
        casefileId,
        currentUserId: currentUser.uid,
        jobs,
        evidenceOptions,
        casefileName
      }, {
        onResultSelect: (result) => {
          // Handle result selection - navigate to the job
          const targetJobId = result.jobId || result.id;
          const targetOrderId = result.orderId || result.order_id;
          if (targetJobId && targetOrderId) {
            setViewState({ mode: 'job', orderId: targetOrderId, jobId: targetJobId });
          }
        }
      });
      
      setSpotsContext(context);
    } catch (error) {
      console.error('Failed to create Spots context:', error);
      setSpotsContext(null);
    }
  }, [currentUser?.uid, casefileId, jobs, evidenceOptions, casefileName]);

  return (
    <div className="casefile-spots-tab">
      {/* Keep the results panel mounted so results persist when opening a job */}
      <div style={{ display: viewState.mode === 'job' ? 'none' : 'block' }}>
        {spotsContext ? (
          <UnifiedResultsPanel
            context={spotsContext}
            searchProvider={spotsContext.searchProvider}
            data={spotsContext.data}
            onResultSelect={spotsContext.onResultSelect}
            showHeader={true}
            autoSearch={true}
          />
        ) : (
          <div className="spots-loading-section">
            <div className="loading-content">
              <h3>Loading Spots Search</h3>
              <p>Preparing advanced search capabilities...</p>
              {!currentUser?.uid && <p className="loading-note">Please log in to use search.</p>}
              {!casefileId && <p className="loading-note">Loading casefile information...</p>}
              {evidenceOptions.length === 0 && <p className="loading-note">Loading evidence files...</p>}
              {!casefileName && <p className="loading-note">Loading case details...</p>}
            </div>
          </div>
        )}
      </div>

      {/* Job view; hidden when not in use so the results panel state is preserved */}
      <div className="spots-single-order" style={{ display: viewState.mode === 'job' ? 'block' : 'none' }}>
        {viewState.orderId && (
          <>
            <div className="tracker-header">
              <button className="btn-secondary" onClick={() => setViewState({ mode: 'list', orderId: null, jobId: null })}>Back to Spots</button>
            </div>
            <SingleOrderResults
              orderId={viewState.orderId}
              focusJobId={viewState.jobId}
              currentUser={currentUser}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CasefileSpotsTab;


